import React, { useMemo, useRef, useState } from 'react';
import { Pills } from '@saleshandy/design-system';

import Input from '../../../../../shared/design-system/components/input';
import Modal from '../../../../../shared/design-system/ui/modal';
import ProspectFiltersSelectDropdown from './prospect-filters-select-dropdown';
import ProspectFiltersMultiSelectDropdown from './prospect-filters-multi-select-dropdown';

import { Team as TeamType } from '../../../../settings/types/users-and-teams';
import { RequestStatus } from '../../../../../shared/enums/request-status';
import { getIsRequestPending, truncate } from '../../../../../shared/utils';
import hasPermission from '../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../shared/utils/access-control/enums/permissions';
import { SavedFilter } from '../../../types';
import RadioButtonGroup from '../../../../../shared/design-system/components/radio-button-group';
import { getUserId } from '../../../../../shared/utils/user-details';

type IProps = {
  show: boolean;
  onHide: VoidFunction;
  onSave: (
    filterName: string,
    isShared: boolean,
    teamIds: number[],
    action: 'save' | 'update',
  ) => void;
  isLoading: boolean;
  teamsList: TeamType[];
  viewDetails?: {
    viewId: number;
    name: string;
    isShared: boolean;
    teams: any[];
  };
  getTeamsListRequestStatus: RequestStatus;
  sendGetTeamsListRequest: VoidFunction;
  selectedSavedView?: SavedFilter;
};

enum SharedWith {
  Public = 'public',
  Private = 'private',
  Team = 'team',
}

const ProspectFiltersSaveAsAViewModal: React.FC<IProps> = ({
  show,
  onHide,
  onSave,
  isLoading,
  viewDetails,

  teamsList,
  getTeamsListRequestStatus,
  sendGetTeamsListRequest,

  selectedSavedView,
}) => {
  const isFiltersDirty = useRef(false);

  const tIds = viewDetails?.teams?.map((team) => team.id) || [];

  const canViewTeamOption =
    hasPermission(Permissions.ACCOUNT_TEAM_MEMBER_READ) ||
    hasPermission(Permissions.TEAM_MEMBER_READ) ||
    hasPermission(Permissions.TEAM_TEAM_MEMBER_READ);

  const getTeamsListRequestStatusLoading = getIsRequestPending(
    getTeamsListRequestStatus,
  );

  const initialSharedWith = () => {
    if (viewDetails?.teams?.length) {
      if (
        teamsList.length === 0 &&
        canViewTeamOption &&
        !getTeamsListRequestStatusLoading
      ) {
        sendGetTeamsListRequest();
      }

      return SharedWith.Team;
    }
    if (viewDetails?.isShared) {
      return SharedWith.Public;
    }

    return SharedWith.Private;
  };

  const [filterName, setFilterName] = useState(viewDetails?.name || '');
  const [filterNameError, setFilterNameError] = useState('');
  const [sharedWith, setSharedWith] = useState<SharedWith>(initialSharedWith());
  const [teamIds, setTeamIds] = useState<number[]>(tIds);
  const [action, setAction] = useState<'save' | 'update'>('save');

  const shareWithOptions = useMemo(
    () =>
      [
        {
          key: SharedWith.Public,
          label: 'Public',
        },
        {
          key: SharedWith.Private,
          label: 'Private (Only me)',
        },
        canViewTeamOption && {
          key: SharedWith.Team,
          label: 'Team',
        },
      ].filter((option) => option),
    [canViewTeamOption],
  );

  const teamsOptions = useMemo(
    () =>
      teamsList?.map((team) => ({
        key: team.id.toString(),
        id: team.id,
        name: team.name,
      })) || [],
    [teamsList],
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      title={viewDetails?.name ? 'Edit View' : 'Save filter'}
      primaryBtnDisabled={
        filterName === '' ||
        filterNameError !== '' ||
        !isFiltersDirty.current ||
        (sharedWith === SharedWith.Team && teamIds?.length === 0)
      }
      primaryBtnLoading={isLoading}
      secondaryBtnDisabled={isLoading}
      primaryBtnClickedHandler={() => {
        onSave(
          filterName,
          sharedWith === SharedWith.Public,
          sharedWith === SharedWith.Team ? teamIds.map((id) => Number(id)) : [],
          action,
        );
      }}
      secondaryBtnClickedHandler={onHide}
      className="prospect-filters-save-as-a-view-modal"
    >
      {!viewDetails?.name && (
        <RadioButtonGroup
          name="save-view"
          value={action}
          options={[
            {
              label: 'Save as new view',
              value: 'save',
            },
            {
              label: 'Update existing view',
              value: 'update',
              disabled:
                !selectedSavedView ||
                getUserId() !== selectedSavedView?.created?.id,
              disabledTooltip: '',
            },
          ]}
          onChange={(value) => {
            setAction(value);

            if (value === 'save') {
              setFilterName('');
            }

            if (value === 'update') {
              isFiltersDirty.current = true;

              setFilterName(selectedSavedView.filterName);

              if (selectedSavedView.isShared) {
                setSharedWith(SharedWith.Public);
              } else if (
                canViewTeamOption &&
                selectedSavedView.teams?.length > 0
              ) {
                setSharedWith(SharedWith.Team);
                setTeamIds(selectedSavedView.teams?.map((team) => team.id));

                if (
                  teamsList.length === 0 &&
                  canViewTeamOption &&
                  !getTeamsListRequestStatusLoading
                ) {
                  sendGetTeamsListRequest();
                }
              } else {
                setSharedWith(SharedWith.Private);
              }
            }
          }}
        />
      )}

      <div className="prospect-filters-save-as-a-view-modal--input-group">
        <p>View Name</p>
        <Input
          placeholder="Enter view name"
          autoFocus
          value={filterName}
          onChange={(value) => {
            setFilterName(value);
            isFiltersDirty.current = true;
            if (value.length > 128 && filterNameError === '') {
              setFilterNameError(
                'Name must not be greater than 128 characters.',
              );
              return;
            }

            if (value.length <= 128 && filterNameError !== '') {
              setFilterNameError('');
            }
          }}
          disabled={action === 'update'}
          variant={filterNameError && Input.Variant.Error}
          caption={filterNameError}
        />
      </div>

      <div className="prospect-filters-save-as-a-view-modal--input-group">
        <p>Share with</p>
        <ProspectFiltersSelectDropdown
          className="w-100"
          options={shareWithOptions}
          optionRenderer={(option) => <span>{option.label}</span>}
          selectedOptionKey={sharedWith}
          selectedOptionRenderer={(option) => <span>{option.label}</span>}
          onChange={(option) => {
            setSharedWith(option.key);

            isFiltersDirty.current = true;

            if (
              teamsList.length === 0 &&
              canViewTeamOption &&
              option.key === SharedWith.Team &&
              !getTeamsListRequestStatusLoading
            ) {
              sendGetTeamsListRequest();
            }
          }}
        />
      </div>

      {canViewTeamOption && sharedWith === SharedWith.Team && (
        <div className="prospect-filters-save-as-a-view-modal--input-group">
          <p>Team</p>
          <ProspectFiltersMultiSelectDropdown
            className="teams-select-box w-100"
            selectClassName="teams-dropdown"
            btnClassName="w-100"
            placeholder="Select Teams"
            options={teamsOptions}
            optionRenderer={(option) => <span>{option?.name}</span>}
            selectedOptionKey={teamIds.map((id) => id.toString())}
            isLoading={getTeamsListRequestStatusLoading}
            showSearch
            filterOption={(search, option) =>
              option?.name?.toLowerCase().includes(search.toLowerCase())
            }
            selectedOptionRenderer={(selectedOptions) => {
              const maxChipsToShow = 2;

              const visibleChips = selectedOptions.slice(0, maxChipsToShow);
              const remainingChips = selectedOptions.slice(
                visibleChips.length - selectedOptions.length,
              );

              if (getTeamsListRequestStatusLoading) {
                return (
                  <span className="bs-select-box-text placeholder">
                    Select Teams
                  </span>
                );
              }

              return (
                <>
                  {visibleChips.map(
                    (option) =>
                      option?.name && (
                        <Pills
                          label={truncate(option?.name || '', 9, false)}
                          size="md"
                          showCloseIcon
                          onClose={() => {
                            isFiltersDirty.current = true;
                            setTeamIds(
                              selectedOptions
                                .filter((item) => item.key !== option.key)
                                .map((item) => item.key),
                            );
                          }}
                        />
                      ),
                  )}

                  {selectedOptions.length > 2 && remainingChips.length > 0 && (
                    <Pills
                      label={`+${remainingChips.length}`}
                      size="md"
                      showCloseIcon
                      onClose={() => {
                        const keysToRemove = remainingChips.map(
                          (item) => item.key,
                        );

                        isFiltersDirty.current = true;
                        setTeamIds(
                          selectedOptions
                            .filter((item) => !keysToRemove.includes(item.key))
                            .map((item) => item.key),
                        );
                      }}
                    />
                  )}
                </>
              );
            }}
            onChange={(options) => {
              setTeamIds(options.map((option) => option.id));

              isFiltersDirty.current = true;
            }}
          />
        </div>
      )}
    </Modal>
  );
};

export default ProspectFiltersSaveAsAViewModal;
